import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Textarea,
} from "@chakra-ui/react";
import { FiInfo } from "react-icons/fi";
import { Help } from "newui/components/ui/Help";
import { buildQuoteTableSteps } from "guides/features/QuoteTable";
import { ConfirmationDialogs } from "./ConfirmationDialogs";
import { SpreadsheetGrid, useColumnManager } from "../SpreadsheetEditor";
import { TemplateSelector } from "./TemplateSelector";
import { BLANK_ROWS, PRESET_OPTIONS } from "./quote-table-constants";
import type {
	ColumnConfig,
	DefaultRowConfig,
	QuoteTableConfig,
} from "./quote-table.types";
import type { QuoteTemplate } from "../../api/quote-templates-api";
import { InputWithLabel, TextAreaWithLabel } from "newui/components/ui/InputWithLabel";
import Tooltip from "components/Tooltip";

// Re-export so consumers importing from this module path keep working.
export type { ColumnConfig, DefaultRowConfig, QuoteTableConfig };

/**
 * Placeholder rows shown in the Create Quote Table preview when the chosen
 * template carries no real row data (Custom / Other / an all-blank template).
 * Preview-only — never submitted; the created table starts empty.
 */
const EXAMPLE_PREVIEW_ROWS = [
	"Example: Excavation works",
	"Example: Concrete footings",
	"Example: Structural steel",
	"Example: Site establishment",
] as const;

export interface UnitOption {
	label: string;
	value: number;
}

export interface CreateQuoteTableModalProps {
	existingColumns?: ColumnConfig[];
	existingDescription?: string;
	/**
	 * Existing row data for edit-mode. Cells must be keyed by the same
	 * `localId` values used in `existingColumns` so description and other
	 * cell values render against the correct columns.
	 */
	existingRows?: DefaultRowConfig[];
	existingTableName?: string;
	isOpen: boolean;
	isSubmitting?: boolean;
	templates?: QuoteTemplate[];
	unitOptions?: UnitOption[];
	onClose: () => void;
	onSubmit: (config: QuoteTableConfig) => void;
}

/**
 * Orchestrator for the quote-table editor modal. Composes `TemplateSelector`,
 * `SpreadsheetGrid`, name/description inputs, and `ConfirmationDialogs` around
 * the `useColumnManager` hook.
 *
 * This file replaces the 897-line monolith — all column state, type
 * constraints, preset application, and cascade-delete logic now live in
 * `useColumnManager`. The modal is purely orchestration: wires hook callbacks
 * to sub-components and owns the local UI state for name/description, preset
 * selection, and the two confirmation dialogs.
 *
 * The output `QuoteTableConfig` shape (and the `CreateQuoteTableModalProps`
 * surface) is preserved exactly so the parent `ProcurerQuoteTab` requires no
 * change.
 */
export const CreateQuoteTableModal = ({
	existingColumns,
	existingDescription,
	existingRows,
	existingTableName,
	isOpen,
	isSubmitting,
	templates,
	onClose,
	onSubmit,
}: CreateQuoteTableModalProps) => {
	const {
		addColumn,
		applyPreset,
		columns,
		duplicateColumn,
		executeColumnDelete,
		getDisabledReason,
		hasUserModified,
		insertColumn,
		isTypeDisabled,
		moveColumn,
		pendingDeleteId,
		removeColumn,
		reorderColumns,
		resetForCreate,
		resetForEdit,
		updateColumnLabel,
		updateColumnType,
	} = useColumnManager();

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [selectedPreset, setSelectedPreset] = useState("blank");
	const [defaultRows, setDefaultRows] =
		useState<DefaultRowConfig[]>(BLANK_ROWS);

	const [isPresetWarningOpen, setIsPresetWarningOpen] = useState(false);
	const [pendingPresetKey, setPendingPresetKey] = useState("");
	const [pendingPresetLabel, setPendingPresetLabel] = useState("");

	const [isTotalRemovalWarningOpen, setIsTotalRemovalWarningOpen] =
		useState(false);
	const [pendingDeleteColumnLabel, setPendingDeleteColumnLabel] = useState("");

	const isEditMode = Boolean(existingColumns?.length);

	// Reset everything on the false→true transition of `isOpen`. A ref guards
	// the body so subsequent renders (typing in the inputs, etc.) don't wipe
	// user state. This pattern lets us include all referenced values in the
	// dep array without re-initialising on every parent re-render.
	const wasOpenRef = useRef(false);
	useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			if (isEditMode) {
				resetForEdit(existingColumns);
				setName(existingTableName ?? "");
				setDescription(existingDescription ?? "");
				setDefaultRows(
					existingRows && existingRows.length > 0 ? existingRows : BLANK_ROWS
				);
			} else {
				resetForCreate();
				setName("");
				setDescription("");
				setSelectedPreset("blank");
				setDefaultRows(BLANK_ROWS);
			}
			setIsPresetWarningOpen(false);
			setIsTotalRemovalWarningOpen(false);
		}
		wasOpenRef.current = isOpen;
	}, [
		isOpen,
		isEditMode,
		existingColumns,
		existingRows,
		existingTableName,
		existingDescription,
		resetForCreate,
		resetForEdit,
	]);

	const handleRemoveColumn = (localId: string) => {
		const result = removeColumn(localId);
		if (result === "needs-confirmation") {
			const col = columns.find((c) => c.localId === localId);
			setPendingDeleteColumnLabel(col?.label ?? "");
			setIsTotalRemovalWarningOpen(true);
		}
		return result;
	};

	const handleTotalRemovalConfirm = () => {
		if (pendingDeleteId) executeColumnDelete(pendingDeleteId);
		setIsTotalRemovalWarningOpen(false);
	};

	const handleTotalRemovalCancel = () => {
		setIsTotalRemovalWarningOpen(false);
	};

	const applyPresetResultToState = (presetKey: string) => {
		const result = applyPreset(presetKey, templates ?? []);
		setDefaultRows(result.rows);
		if (result.name !== undefined) setName(result.name);
		if (result.description !== undefined) setDescription(result.description);
		setSelectedPreset(presetKey);
	};

	const handlePresetChange = (presetKey: string) => {
		if (hasUserModified) {
			const preset = PRESET_OPTIONS.find((p) => p.value === presetKey);
			setPendingPresetLabel(preset?.label ?? presetKey);
			setPendingPresetKey(presetKey);
			setIsPresetWarningOpen(true);
		} else {
			applyPresetResultToState(presetKey);
		}
	};

	const handlePresetConfirm = () => {
		applyPresetResultToState(pendingPresetKey);
		setIsPresetWarningOpen(false);
	};

	const handlePresetCancel = () => {
		setIsPresetWarningOpen(false);
	};

	const isSubmitDisabled =
		name.trim().length === 0 || description.trim().length === 0;

	// Compute preview-rows + supporting infobox for the SpreadsheetGrid.
	// - "Example mode" (no real row data — e.g. Custom / Other / any template
	//   with all-blank rows): show a single italic placeholder row + infobox
	//   explaining the user can add real rows after creating the table.
	// - ">6 rows" mode: show the first 6 rows in the preview + infobox stating
	//   the total row count and that rows are editable post-creation.
	// - Otherwise: show defaultRows as-is.
	const totalRowCount = defaultRows.length;
	// Primary description column (lowest sortOrder among description columns) —
	// used for keying the example preview cell value.
	const primaryDescriptionColumnLocalId = useMemo(() => {
		const descCols = columns
			.filter((c) => c.columnType === "description")
			.sort((a, b) => a.sortOrder - b.sortOrder);
		return descCols[0]?.localId;
	}, [columns]);
	const isExampleMode =
		!isEditMode &&
		defaultRows.every(
			(r) => !Object.values(r.cells ?? {}).some((v) => v?.trim())
		);
	const previewRows: DefaultRowConfig[] = useMemo(() => {
		if (isExampleMode) {
			// Four placeholder rows rather than one: a single row reads as "this
			// table holds one line", whereas the grid is really a multi-row
			// spreadsheet. These are preview-only — they are NOT submitted (see
			// handleSubmit, which sends `defaultRows`), so the created table still
			// starts empty for the user to fill in.
			return EXAMPLE_PREVIEW_ROWS.map((label, index) => ({
				cells: primaryDescriptionColumnLocalId
					? { [primaryDescriptionColumnLocalId]: label }
					: {},
				unit_id: null,
				sort_order: index + 1,
			}));
		}
		if (totalRowCount > 6) {
			return defaultRows.slice(0, 6);
		}
		return defaultRows;
	}, [
		isExampleMode,
		defaultRows,
		totalRowCount,
		primaryDescriptionColumnLocalId,
	]);
	const showManyRowsInfo = !isExampleMode && totalRowCount > 6;

	const handleSubmit = () => {
		const config: QuoteTableConfig = {
			name: name.trim() || "Quote Table",
			description: description.trim(),
			columns,
			presetType: selectedPreset || "custom",
			defaultRows,
		};
		onSubmit(config);
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
			<ModalOverlay />
			<ModalContent minW="min(900px, 95vw)" maxW="95vw">
				<ModalHeader>
					<Flex align="center" gap={2}>
						<span>
							{isEditMode ? "Edit Quote Table" : "Create Quote Table"}
						</span>
						<Help
							guideSteps={buildQuoteTableSteps({ isEditMode })}
							label="Show me how the quote table editor works"
							data-testid="quote-table-help"
						/>
					</Flex>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Box data-testid="quote-table-template-wrapper">
						<TemplateSelector
							isEditMode={isEditMode}
							selectedPreset={selectedPreset}
							templates={templates ?? []}
							onPresetChange={handlePresetChange}
						/>
					</Box>

					<SpreadsheetGrid
						columns={columns}
						defaultRows={previewRows}
						exampleMode={isExampleMode}
						getDisabledReason={getDisabledReason}
						isTypeDisabled={isTypeDisabled}
						onAddColumn={addColumn}
						onDuplicateColumn={duplicateColumn}
						onInsertColumn={insertColumn}
						onMoveColumn={moveColumn}
						onRemoveColumn={handleRemoveColumn}
						onReorderColumns={reorderColumns}
						onUpdateLabel={updateColumnLabel}
						onUpdateType={updateColumnType}
					/>

					{isExampleMode && (
						<Flex
							align="center"
							gap={2}
							mt={2}
							px={3}
							py={2}
							borderRadius="md"
							bg="blue.50"
							color="blue.800"
							fontSize="sm"
							data-testid="quote-table-example-rows-info"
						>
							<FiInfo />
							<span>
								1 row is shown as an example — you can edit these after you
								have created the table.
							</span>
						</Flex>
					)}
					{showManyRowsInfo && (
						<Flex
							align="center"
							gap={2}
							mt={2}
							px={3}
							py={2}
							borderRadius="md"
							bg="blue.50"
							color="blue.800"
							fontSize="sm"
							data-testid="quote-table-many-rows-info"
						>
							<FiInfo />
							<span>
								This table is configured with {totalRowCount} rows in total —
								these can be edited once you have created the table.
							</span>
						</Flex>
					)}
					<InputWithLabel
						className="mt-4"
						label="Table Name" data-testid="quote-table-name-input"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="e.g. Preliminaries"
						required />
					<TextAreaWithLabel
						className="mt-3"
						label="Description" data-testid="quote-table-description-input"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder="What does this table cover? Suppliers will see this to understand what you're asking for."
						rows={3}
						required />

					<ConfirmationDialogs
						isPresetWarningOpen={isPresetWarningOpen}
						isTotalRemovalWarningOpen={isTotalRemovalWarningOpen}
						pendingDeleteColumnLabel={pendingDeleteColumnLabel}
						pendingPresetLabel={pendingPresetLabel}
						onPresetWarningCancel={handlePresetCancel}
						onPresetWarningConfirm={handlePresetConfirm}
						onTotalRemovalCancel={handleTotalRemovalCancel}
						onTotalRemovalConfirm={handleTotalRemovalConfirm}
					/>
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Tooltip label={isSubmitDisabled ? "Please fill in all required fields" : undefined} shouldWrapChildren>
						<Button
							colorScheme="blue"
							ml={3}
							isLoading={isSubmitting}
							isDisabled={isSubmitDisabled}
							onClick={handleSubmit}
						>
							{isEditMode ? "Save Changes" : "Create Table"}
						</Button>
					</Tooltip>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default CreateQuoteTableModal;
